import type { User, AuditEntry } from '../types'
import { apiFetch, getAuthHeader, handleUnauthorized, downloadBlob } from './core'

export interface AdminUserProfile {
  username: string
  initials: string | null
  profiles: {
    id: number
    name: string
    is_active: boolean
    project_number: string | null
    sender_gln: string | null
    sender_name: string | null
    receiver_gln: string | null
    receiver_name: string | null
  }[]
}

export async function getUsers(): Promise<User[]> {
  return apiFetch<User[]>('/api/users')
}

export async function createUser(data: {
  username: string
  password: string
  email?: string
  initials?: string
  is_admin?: boolean
}): Promise<User> {
  return apiFetch<User>('/api/users', { method: 'POST', body: JSON.stringify(data) })
}

export async function updateUser(
  id: number,
  data: { email?: string; initials?: string; is_admin?: boolean; is_active?: boolean; password?: string; mfa_required?: boolean },
): Promise<User> {
  return apiFetch<User>(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export async function deleteUser(id: number): Promise<void> {
  return apiFetch<void>(`/api/users/${id}`, { method: 'DELETE' })
}

export async function resetUserMfa(id: number): Promise<void> {
  return apiFetch<void>(`/api/users/${id}/mfa`, { method: 'DELETE' })
}

export async function resendWelcomeEmail(id: number): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/users/${id}/welcome-email`, { method: 'POST' })
}

export async function getLogs(lines: number = 200): Promise<{ lines: string[]; source: string }> {
  return apiFetch(`/api/admin/logs?lines=${lines}`)
}

export async function getBccSetting(): Promise<string> {
  const res = await apiFetch<{ bcc: string }>('/api/admin/setting/bcc')
  return res.bcc
}

export async function setBccSetting(bcc: string): Promise<string> {
  const res = await apiFetch<{ bcc: string }>('/api/admin/setting/bcc', {
    method: 'PUT',
    body: JSON.stringify({ bcc }),
  })
  return res.bcc
}

export async function getFunFactsSetting(): Promise<boolean> {
  const res = await apiFetch<{ enabled: boolean }>('/api/admin/setting/fun-facts')
  return res.enabled
}

export async function setFunFactsSetting(enabled: boolean): Promise<boolean> {
  const res = await apiFetch<{ enabled: boolean }>('/api/admin/setting/fun-facts', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  })
  return res.enabled
}

export async function getMaintenanceModeSetting(): Promise<boolean> {
  const res = await apiFetch<{ enabled: boolean }>('/api/admin/setting/maintenance-mode')
  return res.enabled
}

export async function setMaintenanceModeSetting(enabled: boolean): Promise<boolean> {
  const res = await apiFetch<{ enabled: boolean }>('/api/admin/setting/maintenance-mode', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  })
  return res.enabled
}

export async function getAuditLog(params: {
  limit?: number
  offset?: number
  username?: string
  action?: string
}): Promise<{ total: number; entries: AuditEntry[] }> {
  const q = new URLSearchParams()
  if (params.limit)    q.set('limit',    String(params.limit))
  if (params.offset)   q.set('offset',   String(params.offset))
  if (params.username) q.set('username', params.username)
  if (params.action)   q.set('action',   params.action)
  return apiFetch(`/api/admin/audit-log?${q}`)
}

export async function getAdminUserProfiles(): Promise<AdminUserProfile[]> {
  return apiFetch<AdminUserProfile[]>('/api/admin/user-profiles')
}

export async function downloadBackup(): Promise<void> {
  const res = await fetch('/api/admin/backup', {
    headers: { Authorization: getAuthHeader() },
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  await downloadBlob(res, 'xmlinsights-backup.zip')
}

export async function restoreBackup(file: File): Promise<{ message: string }> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch('/api/admin/restore', {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}
